import React from "react";
import { Link } from "react-router-dom";
import { Calendar, Check, Clock, ArrowRight, CircleDot } from "lucide-react";

const proposals = [
  {
    title: "Leg Day",
    when: "Mon · 6:00 PM",
    where: "Iron Republic",
    other: "Marcus",
    status: "Proposed",
    statusTone: "warn",
  },
  {
    title: "Run & Drinks",
    when: "Wed · 7:00 AM",
    where: "Beltline · 5K",
    other: "Nia",
    status: "Accepted",
    statusTone: "ok",
  },
  {
    title: "CrossFit WOD",
    when: "Sat · 9:00 AM",
    where: "BoxOne CF",
    other: "DeAndre",
    status: "In Progress",
    statusTone: "live",
  },
];

const statusStyles = {
  warn: "bg-amber-300/15 text-amber-300 border-amber-300/30",
  ok: "bg-[#C6FF00]/15 text-[#C6FF00] border-[#C6FF00]/40",
  live: "bg-white/10 text-white border-white/20",
};

const SetTest = () => {
  return (
    <section
      id="set-test"
      data-testid="set-test-section"
      className="relative py-24 md:py-32 bg-[#0A0A0A] overflow-hidden"
    >
      <div className="absolute -left-32 top-1/3 w-[420px] h-[420px] rounded-full bg-[#C6FF00]/10 blur-[140px] pointer-events-none" />

      <div className="relative container mx-auto max-w-7xl px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          {/* Left text */}
          <div className="lg:col-span-5">
            <p className="text-xs uppercase tracking-[0.3em] text-[#C6FF00] font-semibold mb-4">
              Core Mechanic
            </p>
            <h2 className="font-heading text-5xl md:text-6xl lg:text-7xl text-white leading-[0.95]">
              Pass The
              <br />
              <span className="text-[#C6FF00]">Set Test.</span>
            </h2>
            <p className="mt-6 text-base md:text-lg text-neutral-300 max-w-md leading-relaxed">
              Matching is easy. <span className="text-white font-semibold">Showing up</span> is
              what matters.
            </p>
            <p className="mt-4 text-sm text-neutral-400 max-w-md leading-relaxed">
              After you match, propose a real workout — leg day, a run, a class. The other
              person accepts or declines. The Set Test turns swipes into the start of
              something real.
            </p>

            <Link
              to="/register"
              data-testid="set-test-cta-btn"
              className="mt-10 inline-flex items-center gap-2 bg-[#C6FF00] hover:bg-[#AEE600] text-[#0A0A0A] font-bold text-sm uppercase tracking-[0.25em] px-8 py-4 rounded-md transition-all hover:scale-105 shadow-[0_0_24px_rgba(198,255,0,0.35)] hover:shadow-[0_0_44px_rgba(198,255,0,0.55)]"
            >
              Set a Workout <ArrowRight size={16} />
            </Link>

            <div className="mt-10 grid grid-cols-3 gap-4 max-w-md">
              {[
                { label: "Propose", v: "01" },
                { label: "Confirm", v: "02" },
                { label: "Show up", v: "03" },
              ].map((s) => (
                <div key={s.label} className="border-l-2 border-[#C6FF00]/50 pl-3">
                  <div className="font-heading text-2xl text-white">{s.v}</div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-semibold mt-0.5">
                    {s.label}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right scheduling cards */}
          <div className="lg:col-span-7 space-y-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[11px] uppercase tracking-[0.25em] text-neutral-500 font-semibold">
                Your Set Tests
              </span>
              <span className="font-heading text-2xl text-[#C6FF00]">03</span>
            </div>

            {proposals.map((p, i) => (
              <div
                key={p.title}
                data-testid={`set-test-card-${i}`}
                className="group bg-[#111] border border-white/5 hover:border-[#C6FF00]/40 rounded-2xl p-5 md:p-6 flex items-center gap-5 transition-all duration-300"
              >
                <div className="hidden sm:flex flex-col items-center justify-center w-14 h-14 rounded-xl bg-[#C6FF00]/10 border border-[#C6FF00]/30 text-[#C6FF00] flex-shrink-0">
                  <Calendar size={20} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-heading text-2xl md:text-3xl text-white tracking-wide leading-none">
                      {p.title}
                    </h3>
                    <span
                      className={`px-2 py-0.5 rounded-full border text-[9px] font-bold uppercase tracking-[0.15em] flex items-center gap-1 ${statusStyles[p.statusTone]}`}
                    >
                      {p.statusTone === "ok" && <Check size={10} />}
                      {p.statusTone === "live" && <CircleDot size={10} className="animate-pulse" />}
                      {p.status}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 text-[11px] uppercase tracking-widest text-neutral-400 mt-2">
                    <span className="flex items-center gap-1">
                      <Clock size={11} /> {p.when}
                    </span>
                    <span className="text-neutral-600">·</span>
                    <span className="truncate">{p.where}</span>
                  </div>
                  <div className="text-xs text-neutral-300 mt-2">
                    with{" "}
                    <span className="text-[#C6FF00] font-semibold">{p.other}</span>
                  </div>
                </div>
                <div className="hidden md:flex flex-col items-end gap-1.5">
                  {p.status === "Proposed" && (
                    <>
                      <button
                        type="button"
                        className="px-4 py-1.5 rounded-md bg-[#C6FF00] text-[#0A0A0A] text-[10px] font-bold uppercase tracking-[0.15em]"
                      >
                        Accept
                      </button>
                      <button
                        type="button"
                        className="px-4 py-1.5 rounded-md border border-white/15 text-neutral-300 text-[10px] font-semibold uppercase tracking-[0.15em] hover:border-white/30"
                      >
                        Decline
                      </button>
                    </>
                  )}
                  {p.status === "Accepted" && (
                    <span className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-semibold">
                      24h reminder
                    </span>
                  )}
                  {p.status === "In Progress" && (
                    <span className="text-[10px] uppercase tracking-[0.2em] text-[#C6FF00] font-semibold flex items-center gap-1">
                      <CircleDot size={10} className="animate-pulse" /> Started
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default SetTest;
